import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Input, TextArea } from "common/InputForm";
import { CustomButton } from "components/form";
import useButtonLoader from "common/UseButtonLoader";
import useDebounce from "common/UseDebounce";
import { toastMessage } from "utils/toastMessage";
import { regex } from "utils/formValidator";
import { addPostApi, postHastagListApi } from "services/post";
import { IoCloudUploadOutline } from "react-icons/io5";
import { FaImage, FaTimes } from "react-icons/fa";
import { GoX } from "react-icons/go";

const AddPost = () => {
  const [buttonLoaders, setButtonLoaders] = useButtonLoader("Save");
  const [errors, setErrors] = useState({});
  const { imageFileRegex, videoFileRegex } = regex;
  const [filteredHashtagList, setFilteredHashtagList] = useState([]);
  const [currentWord, setCurrentWord] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    caption: "",
    location: "",
    images: [],
    video: "",
    videoName: "",
    videoThumb: "",
    hashTags: "",
  });
  const [hashtagList, setHashtagList] = useState([]);
  const [search, setSearch] = useState("");
  console.log(hashtagList, "hashtagList");
  const debouncedValue = useDebounce(search, 300);
  const editor = useRef(null);
  // Editor Config
  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: "Enter your caption...",
      toolbar: true,
      spellcheck: true,
      language: "en",
      toolbarButtonSize: "medium",
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: false,
      uploader: { insertImageAsBase64URI: true },
    }),
    []
  );
  const handleahastag = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, hashTags: value }));
    setSearch(value);
  };
  // Fetch hashtag suggestions
  useEffect(() => {
    const fetchHashtagList = async () => {
      try {
        const res = await postHastagListApi();
        if (res?.data?.status === 200) {
          setHashtagList(res.data?.data?.hashtags || []);
        } else {
          toastMessage(res?.data?.message, "error", "hashtagList");
        }
      } catch (e) {
        toastMessage(e.message, "error", "hashtagList");
      }
    };
    fetchHashtagList();
  }, []);
  // Filter the hashtag list based on user input
  // useEffect(() => {
  //   if (debouncedValue) {
  //     const filteredList = hashtagList.filter((tag) =>
  //       tag.title.toLowerCase().includes(debouncedValue.toLowerCase())
  //     );
  //     setFilteredHashtagList(filteredList);
  //   } else {
  //     setFilteredHashtagList([]);
  //   }
  // }, [debouncedValue, hashtagList]);

  const handleHashtagChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, hashTags: value }));
    setSearch(value);
  };

  const checkHashtag = (value) => {
    // Check if the user typed `#` or is typing a hashtag
    const words = value.split(" ");
    const lastWord = words[words.length - 1];

    if (lastWord.startsWith("")) {
      setCurrentWord(lastWord.substring(1)); // Get the word after `#`
      setFilteredHashtagList(
        hashtagList.filter((tag) =>
          tag.title.toLowerCase().includes(currentWord.toLowerCase())
        )
      );
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    if (name === "caption") {
      checkHashtag(formData?.caption);
    }
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => imageFileRegex.test(file.name));
    const imageUrls = validFiles.map((file) => URL.createObjectURL(file));
    setFormData((prevData) => ({
      ...prevData,
      images: [...prevData.images, ...validFiles],
    }));
  };

  // Remove an image
  const removeImage = (index) => {
    setFormData((prevData) => {
      const updatedImages = prevData.images.filter((_, i) => i !== index);
      return { ...prevData, images: updatedImages };
    });
  };

  // Handle video upload and thumbnail generation
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file && videoFileRegex.test(file.name)) {
      const validFiles = videoFileRegex.test(file.name);

      const videoUrl = URL.createObjectURL(file);
      setFormData((prevData) => ({
        ...prevData,
        video: videoUrl,
        videoName: file,
      }));
      // Generate thumbnail
      const videoElement = document.createElement("video");
      videoElement.src = videoUrl;
      videoElement.currentTime = 1; // Capture thumbnail at 1-second mark
      videoElement.onloadeddata = () => {
        const canvas = document.createElement("canvas");
        canvas.width = videoElement.videoWidth / 2;
        canvas.height = videoElement.videoHeight / 2;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        const thumbnail = canvas.toDataURL("image/png");
        setFormData((prevData) => ({ ...prevData, videoThumb: thumbnail }));
      };
    }
  };
  const handleRemoveVideo = () => {
    setFormData((prevData) => ({
      ...prevData,
      video: "",
      videoName: null,
      videoThumb: "",
    }));
  };

  const emptyDescriptionRegex = /^<p><br><\/p>$/;
  // Handle form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData?.caption || emptyDescriptionRegex.test(formData.caption)) {
      newErrors.caption = "Caption is required";
    }

    // if (!formData.location) {
    //   newErrors.location = "Location is required";
    // }

    // Enforce that only one of video or image(s) can be uploaded
    if (formData.video && formData.images && formData.images.length > 0) {
      newErrors.general =
        "You can only upload either images or a video, not both";
    } else if (
      !formData.video &&
      (!formData.images || formData.images.length === 0)
    ) {
      newErrors.generals = "You must upload at least one image or a video";
    }
    console.log(newErrors, "newErrors");
    // else if (formData.images && formData.images.length > 1) {
    //   newErrors.images = "You can only upload one image";
    // }

    // Add additional validation as needed
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Submit form data
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setButtonLoaders(true);
    try {
      const formDatas = new FormData();
      formDatas.append("caption", formData.caption);
      formDatas.append("location", formData.location || "");
      formDatas.append("video", formData.videoName || "");
      formDatas.append("videoThumb", formData.videoThumb || "");
      formDatas.append("hashTags", formData.hashTags || "");
      if (formData.images && Array.isArray(formData.images)) {
        formData.images.forEach((image) => {
          formDatas.append("images", image);
        });
      } else {
        formDatas.append("images", "");
      }

      const res = await addPostApi(formDatas);
      if (res?.data?.status === 200) {
        toastMessage(res?.data?.message, "success", "addPost");
        navigate(-1);
      } else {
        toastMessage(res?.data?.message, "error", "addPost");
      }
    } catch (e) {
      toastMessage(e.message, "error", "addPost");
    } finally {
      setButtonLoaders(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const words = formData.caption.split(" ");
    if (words?.length) {
      words[words?.length - 1] = `${suggestion}`; // Replace the current word with the selected suggestion
    }
    setFormData({ ...formData, caption: words.join(" ") });
    setShowSuggestions(false);
  };

  console.log(filteredHashtagList, "filteredHashtagList");
  return (
    <div className="addClass">
      <div className="dashboard_title has_child">
        <h3>
          <Link to={-1}>
            <IoMdArrowRoundBack />
          </Link>{" "}
          Add Post
        </h3>
        <CustomButton
          type="button"
          className="button"
          ref={buttonLoaders}
          onClick={handleSubmit}
        >
          Save and Submit
        </CustomButton>
      </div>
      <div className="card">
        <div className="card-body">
          <form>
            <div className="form_field_wrap">
              <div className="form_field">
                <Input
                  type="text"
                  label="Location"
                  name="location"
                  placeholder="Enter Location"
                  value={formData.location}
                  onChange={handleInputChange}
                  error={errors.location}
                />
              </div>
              <div className="form_field">
                <Input
                  value={formData?.caption}
                  name="caption"
                  error={errors.caption}
                  onChange={handleInputChange}
                  label={"Caption"}
                />
                {showSuggestions && (
                  <ul
                    style={{
                      position: "absolute",
                      background: "white",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      listStyle: "none",
                      padding: "10px",
                      margin: 0,
                      top: "100%",
                      left: 0,
                      width: "100%",
                      zIndex: 10,
                    }}
                  >
                    {filteredHashtagList.map(({ title }, index) => (
                      <li
                        key={index}
                        style={{
                          padding: "5px 10px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                        onClick={() => handleSuggestionClick(title)}
                      >
                        {title}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* <div className="form_field">
                <Input
                  type="text"
                  label="Hashtags"
                  name="hashTags"
                  placeholder="Enter Hashtags"
                  value={formData.hashTags}
                  onChange={handleHashtagChange}
                  list="hashtag-options"
                />
                <div>
                  {filteredHashtagList.map((tag, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        // Update the formData hashTags value when a suggestion is clicked
                        setFormData((prev) => ({
                          ...prev,
                          hashTags: tag.title,
                        }));
                        setFilteredHashtagList([]);
                      }}
                      style={{
                        cursor: "pointer",
                        padding: "8px",
                        borderBottom: "1px solid #ddd",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      {tag.title}
                    </div>
                  ))}
                </div>
              </div> */}
            </div>
            <div className="form_field_wrap">
              <div className="form_field">
                <label className="label">Upload Images</label>
                <label className="file_uploader" htmlFor="upload_image">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    id="upload_image"
                  />
                  {formData.images.length > 0 ? (
                    <>
                      <FaImage className="icon" />
                      <h5>Upload Images</h5>
                    </>
                  ) : (
                    <>
                      {" "}
                      <IoCloudUploadOutline className="icon" />
                      <h5>Upload Images</h5>
                    </>
                  )}
                </label>
                <div className="image_preview_wrap">
                  {formData.images.map((image, index) => (
                    <div key={index} className="image_preview">
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`Preview ${index}`}
                      />
                      <button type="button" onClick={() => removeImage(index)}>
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="form_field">
                <div className="upload_button_wrap">
                  <label htmlFor="profile_image" className="upload_button">
                    Upload Video
                  </label>
                  <label className="file_uploader" htmlFor="upload_video">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoChange}
                      id="upload_video"
                    />

                    <div className="text-center">
                      {formData.video.length > 0 ? (
                        <div className="video_preview">
                          <video src={formData.video} controls width="300" />
                          <button
                            className="remove_video_button"
                            onClick={handleRemoveVideo}
                          >
                            <GoX />
                          </button>
                        </div>
                      ) : (
                        <>
                          <IoCloudUploadOutline className="icon" />
                          <h5>Upload Video</h5>
                        </>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>
            {errors && (
              <p className="invalid-feedback d-block">{errors.general}</p>
            )}
            {errors && (
              <p className="invalid-feedback d-block">{errors.generals}</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPost;
